import { api, ServerError } from './api';

/*
 * Driving the card reader.
 *
 * Only the local agent has a terminal — the cloud API does not, and the same
 * bundle runs against both. So every call here treats "no terminal" as a normal
 * answer and the caller falls back to entering the reference by hand, which is
 * what a shop with a standalone کارتخوان does anyway.
 */

/** What the agent says happened. `unknown` is the one that matters. */
export type ChargeOutcome = 'approved' | 'declined' | 'unknown' | 'unavailable';

export interface ChargeResult {
  outcome: ChargeOutcome;
  rrn?: string;
  cardMask?: string;
  message: string;
  /** True when a سرشیفت must resolve this before the basket can be paid. */
  needsDecision: boolean;
  /*
   * True when this basket was charged a moment ago under a different sale.
   *
   * Nothing is wrong and nothing is locked — the amount has not reached the
   * reader. The cashier answers whether this is a second customer, and a yes
   * re-sends with confirmDuplicate.
   */
  needsConfirmation?: boolean;
}

export interface PendingAttempt {
  invoiceId: string;
  amountRial: number;
  startedAt: string;
  rrn: string;
}

/*
 * Charge sends the amount and waits.
 *
 * The basket's own reference is the invoice id, deliberately: the guard keys
 * its double-charge block on it, and the thing that must not be paid twice is
 * the basket — before any invoice exists.
 *
 * There is no client-side timeout. The agent owns the deadline, because it is
 * the only side that knows whether the amount reached the terminal; a browser
 * that gave up early would show "failed" over a payment still in progress.
 */
export async function charge(
  basketRef: string,
  amountToman: number,
  fingerprint?: string,
  confirmDuplicate?: boolean,
): Promise<ChargeResult> {
  try {
    const res = await api.post<{
      state: string;
      outcome: string;
      rrn?: string;
      cardMask?: string;
      needsDecision: boolean;
      needsConfirmation?: boolean;
      message: string;
    }>('/terminal/charge', {
      invoiceId: basketRef,
      amountToman,
      ...(fingerprint ? { fingerprint } : {}),
      ...(confirmDuplicate ? { confirmDuplicate: true } : {}),
    });

    return {
      outcome: (res.outcome as ChargeOutcome) ?? 'unknown',
      rrn: res.rrn,
      cardMask: res.cardMask,
      needsDecision: res.needsDecision,
      needsConfirmation: res.needsConfirmation,
      message: res.message,
    };
  } catch (e) {
    /*
     * A 404 means this deployment has no terminal — the cloud build, or a till
     * configured for cash only. That is not a failure and must not read as
     * one: the cashier takes the payment on a standalone reader and types the
     * reference.
     */
    if (e instanceof ServerError && e.status === 404) {
      return {
        outcome: 'unavailable',
        needsDecision: false,
        message: 'کارت‌خوان به این صندوق وصل نیست — مبلغ را روی دستگاه بزنید و شماره پیگیری را وارد کنید',
      };
    }
    /*
     * A duplicate question, which the agent answers with 409 and a body.
     *
     * Read before the unknown case below: this one is NOT unknown. The amount
     * never reached the reader, so nothing is in doubt — the cashier just has
     * to say whether this is a second customer.
     */
    if (
      e instanceof ServerError &&
      e.status === 409 &&
      e.body?.needsConfirmation === true
    ) {
      return {
        outcome: 'declined',
        needsDecision: false,
        needsConfirmation: true,
        message: e.detail ?? e.message,
      };
    }
    /*
     * Anything else is unknown, not declined.
     *
     * The request may have reached the agent and the agent the terminal. A
     * browser cannot tell a refused connection from a response that was lost
     * on the way back, and guessing "declined" is what lets a cashier charge
     * the customer twice.
     */
    return {
      outcome: 'unknown',
      needsDecision: true,
      message:
        e instanceof ServerError
          ? (e.detail ?? e.message)
          : 'نتیجه پرداخت نامشخص است — رسید دستگاه کارت‌خوان را ببینید',
    };
  }
}

/** Payments awaiting a سرشیفت's decision. */
export async function pending(): Promise<PendingAttempt[]> {
  try {
    return await api.get<PendingAttempt[]>('/terminal/pending');
  } catch {
    // No terminal, or no agent. An empty list is right: there is nothing this
    // till is holding.
    return [];
  }
}

/** Record a سرشیفت's verdict on an ambiguous payment. */
export async function resolve(
  invoiceId: string,
  resolution: 'charged' | 'not_charged',
  resolvedBy: string,
): Promise<string> {
  const res = await api.post<{ message: string }>('/terminal/resolve', {
    invoiceId,
    resolution,
    resolvedBy,
  });
  return res.message;
}
